import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  PlayCircle, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  User, Download, Square, Loader2, Archive, Trash2, Info, FileText,
  BarChart3, Clock, ChevronDown, ChevronUp, Sparkles, PenLine, Send
} from 'lucide-react';

const SEVERITY_CONFIG = {
  ok: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Nessun problema' },
  minor: { color: 'bg-blue-100 text-blue-700', icon: AlertTriangle, label: 'Minore' },
  major: { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle, label: 'Rilevante' },
  blocking: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Bloccante' },
};

const SEVERITY_FILTER_MAP = {
  all: { label: 'Tutti', values: null },
  blocking: { label: 'Bloccanti', values: ['blocking', 'urgent'] },
  major: { label: 'Rilevanti', values: ['major', 'short'] },
  minor: { label: 'Minori', values: ['minor', 'pending'] },
  ok: { label: 'Nessun problema', values: ['ok'] },
};

function getSeverityLabel(sev) {
  const map = { blocking: 'Bloccante', urgent: 'Bloccante', major: 'Rilevante', short: 'Rilevante', minor: 'Minore', pending: 'Minore', ok: 'Nessun problema', idea: 'Suggerimento' };
  return map[sev] || sev;
}

function getSeverityConfig(sev) {
  if (sev === 'blocking' || sev === 'urgent') return SEVERITY_CONFIG.blocking;
  if (sev === 'major' || sev === 'short') return SEVERITY_CONFIG.major;
  if (sev === 'minor' || sev === 'pending') return SEVERITY_CONFIG.minor;
  return SEVERITY_CONFIG.ok;
}

export default function TesterPage() {
  const [scenarios, setScenarios] = useState([]);
  const [personas, setPersonas] = useState({});
  const [sessions, setSessions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionShownAt, setSessionShownAt] = useState(null);
  const [running, setRunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionHours, setSessionHours] = useState(4);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [clusterTab, setClusterTab] = useState('trasversale');
  const [generatingSynthesis, setGeneratingSynthesis] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [customText, setCustomText] = useState('');
  const [runningCustom, setRunningCustom] = useState(false);
  const exportRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [sc, ps, ss, fd, st, kpi] = await Promise.all([
        api.get('/tester/scenarios'), api.get('/tester/personas'),
        api.get('/tester/sessions'), api.get('/tester/findings'),
        api.get('/tester/session-status'), api.get('/tester/kpi-summary')
      ]);
      setScenarios(sc.data); setPersonas(ps.data);
      setSessions(ss.data); setFindings(fd.data);
      setSessionStatus(st.data); setKpiSummary(kpi.data);
      if (sc.data.length > 0 && !selectedScenario) setSelectedScenario(sc.data[0].id);
    } catch { toast.error('Errore caricamento tester'); }
    setLoading(false);
  }, [selectedScenario]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-hide session results after 10 minutes
  useEffect(() => {
    if (!selectedSession || !sessionShownAt) return;
    const timer = setTimeout(() => {
      setSelectedSession(null);
      setSessionShownAt(null);
    }, 10 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [selectedSession, sessionShownAt]);

  // Close export menu on outside click
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runScenario = async (scenarioId) => {
    setRunning(scenarioId);
    try {
      const res = await api.post(`/tester/run-scenario/${scenarioId}`);
      setSelectedSession(res.data);
      setSessionShownAt(Date.now());
      toast.success(`Scenario completato — UX score: ${res.data.result?.overall_ux_score || 'N/A'}`);
      const [ss, fd, kpi] = await Promise.all([api.get('/tester/sessions'), api.get('/tester/findings'), api.get('/tester/kpi-summary')]);
      setSessions(ss.data); setFindings(fd.data); setKpiSummary(kpi.data);
    } catch { toast.error('Errore esecuzione scenario'); }
    setRunning(null);
  };

  const startFullSession = async () => {
    try {
      const r = await api.post('/tester/run-all', { stop_after_hours: sessionHours });
      setSessionStatus({ is_running: true, session: r.data });
      toast.success(`Sessione avviata — ${r.data.scenarios_count} scenari`);
      setTimeout(loadAll, 3000);
    } catch { toast.error('Errore avvio sessione'); }
  };

  const stopSession = async () => {
    try {
      await api.post('/tester/stop');
      setSessionStatus({ is_running: false });
      toast.success('Sessione fermata');
      loadAll();
    } catch { toast.error('Errore stop sessione'); }
  };

  const downloadReport = async (sessionId, format = 'markdown') => {
    try {
      const response = await api.get(`/tester/sessions/${sessionId}/export?format=${format}`, { responseType: 'blob' });
      const ext = format === 'json' ? 'json' : 'md';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tester_report_${sessionId.slice(0, 8)}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download report'); }
  };

  const moveToBacklog = async (findingId) => {
    try {
      await api.patch(`/tester/findings/${findingId}/backlog`, { priority: 'pending' });
      setFindings(prev => prev.filter(f => f.id !== findingId));
      toast.success('Archiviato tra gli elementi da valutare');
    } catch { toast.error('Errore'); }
  };

  const deleteFinding = async (findingId) => {
    try {
      await api.delete(`/tester/findings/${findingId}`);
      setFindings(prev => prev.filter(f => f.id !== findingId));
      toast.success('Problema eliminato');
    } catch { toast.error('Errore eliminazione'); }
  };

  const exportFindings = async (severity = null) => {
    try {
      const params = severity ? `?severity=${severity}` : '';
      const response = await api.get(`/tester/findings/export${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = severity ? `_${severity}` : '';
      link.setAttribute('download', `report_problemi${suffix}.md`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report problemi esportato');
      setExportMenuOpen(false);
    } catch { toast.error('Errore esportazione problemi'); }
  };

  const generateSynthesis = async () => {
    setGeneratingSynthesis(true);
    try {
      const response = await api.get('/tester/synthesis', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sintesi_qualita_ux.md');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sintesi qualita generata e scaricata');
    } catch { toast.error('Errore generazione sintesi'); }
    setGeneratingSynthesis(false);
  };

  const runCustomScenario = async () => {
    if (!customText.trim()) return;
    setRunningCustom(true);
    try {
      const res = await api.post('/tester/run-custom', { description: customText.trim() });
      setSelectedSession(res.data);
      setSessionShownAt(Date.now());
      toast.success(`Scenario personalizzato completato — UX score: ${res.data.result?.overall_ux_score || 'N/A'}`);
      const [ss, fd, kpi] = await Promise.all([api.get('/tester/sessions'), api.get('/tester/findings'), api.get('/tester/kpi-summary')]);
      setSessions(ss.data); setFindings(fd.data); setKpiSummary(kpi.data);
    } catch { toast.error('Errore esecuzione scenario personalizzato'); }
    setRunningCustom(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-[#2E75B6]" /></div>;

  const currentSession = selectedSession || sessions.find(s => s.scenario_id === selectedScenario);
  const result = currentSession?.result || {};

  const filteredScenarios = clusterTab === 'custom' ? [] : scenarios.filter(s => s.cluster === clusterTab || (!s.cluster && clusterTab === 'trasversale'));
  const trasversaleCount = scenarios.filter(s => s.cluster === 'trasversale' || !s.cluster).length;
  const sezioneCount = scenarios.filter(s => s.cluster === 'sezione').length;

  const filteredFindings = findings.filter(f => {
    if (severityFilter === 'all') return true;
    const allowed = SEVERITY_FILTER_MAP[severityFilter]?.values;
    return allowed ? allowed.includes(f.severity) : true;
  });

  const severityCounts = {
    all: findings.length,
    blocking: findings.filter(f => ['blocking', 'urgent'].includes(f.severity)).length,
    major: findings.filter(f => ['major', 'short'].includes(f.severity)).length,
    minor: findings.filter(f => ['minor', 'pending'].includes(f.severity)).length,
    ok: findings.filter(f => f.severity === 'ok').length,
  };

  // Time remaining for auto-hide
  const autoHideRemaining = sessionShownAt ? Math.max(0, Math.round((sessionShownAt + 10 * 60 * 1000 - Date.now()) / 60000)) : null;

  return (
    <div className="space-y-5" data-testid="tester-page">
      {/* Header — Quality Monitoring Center */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A3A5C]">Centro Monitoraggio Qualita</h1>
          <p className="text-sm text-[#64748B] mt-0.5">Simulazione automatica, rilevazione criticita e report di sintesi</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!sessionStatus?.is_running ? (
            <div className="flex items-center gap-1.5">
              <Input type="number" min={1} max={24} value={sessionHours}
                onChange={(e) => setSessionHours(Number(e.target.value))}
                className="w-14 text-sm text-center h-8"
                data-testid="session-hours-input" />
              <span className="text-xs text-[#64748B]">ore</span>
              <Button size="sm" className="h-8 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white"
                onClick={startFullSession} data-testid="start-session-btn">
                <PlayCircle className="w-3.5 h-3.5 mr-1" />Avvia sessione completa
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700 text-xs animate-pulse">In esecuzione</Badge>
              <Button size="sm" variant="destructive" className="h-8"
                onClick={stopSession} data-testid="stop-session-btn">
                <Square className="w-3.5 h-3.5 mr-1" />Ferma
              </Button>
            </div>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={generateSynthesis} disabled={generatingSynthesis || findings.length === 0}
            data-testid="generate-synthesis-btn">
            {generatingSynthesis ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Genera sintesi
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={async () => {
              try {
                const r = await api.get('/tester/findings/export-pdf', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement('a'); a.href = url;
                a.setAttribute('download', 'problemi_tester_ux.pdf');
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('PDF esportato');
              } catch { toast.error('Errore export PDF'); }
            }}
            disabled={findings.length === 0}
            data-testid="export-findings-pdf-btn">
            <FileText className="w-3.5 h-3.5 mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      {kpiSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tester-kpi-summary">
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#1A3A5C]">{kpiSummary.scenarios_total}</p>
            <p className="text-[10px] text-[#64748B]">Scenari disponibili</p>
          </CardContent></Card>
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#1A3A5C]">{kpiSummary.sessions_total}</p>
            <p className="text-[10px] text-[#64748B]">Sessioni eseguite</p>
          </CardContent></Card>
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${kpiSummary.avg_ux_score >= 7 ? 'text-emerald-600' : kpiSummary.avg_ux_score >= 5 ? 'text-amber-600' : 'text-red-600'}`}>
              {kpiSummary.avg_ux_score || '-'}<span className="text-sm">/10</span>
            </p>
            <p className="text-[10px] text-[#64748B]">Score UX medio</p>
          </CardContent></Card>
          <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#1A3A5C]">{kpiSummary.findings_open}</p>
            <p className="text-[10px] text-[#64748B]">Problemi aperti</p>
          </CardContent></Card>
          <Card className={`border-[#E2E8F0] ${kpiSummary.blocking_count > 0 ? 'border-l-4 border-l-red-400' : ''}`}><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${kpiSummary.blocking_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{kpiSummary.blocking_count}</p>
            <p className="text-[10px] text-[#64748B]">Bloccanti</p>
          </CardContent></Card>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Left — Scenarios */}
        <div className="col-span-3 space-y-3">
          <div className="flex border-b border-gray-200" data-testid="cluster-tabs">
            <button
              onClick={() => setClusterTab('trasversale')}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${clusterTab === 'trasversale' ? 'border-[#2E75B6] text-[#1A3A5C]' : 'border-transparent text-[#64748B] hover:text-[#1A3A5C]'}`}
              data-testid="tab-trasversale">
              Trasversali ({trasversaleCount})
            </button>
            <button
              onClick={() => setClusterTab('sezione')}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${clusterTab === 'sezione' ? 'border-[#2E75B6] text-[#1A3A5C]' : 'border-transparent text-[#64748B] hover:text-[#1A3A5C]'}`}
              data-testid="tab-sezione">
              Per sezione ({sezioneCount})
            </button>
            <button
              onClick={() => setClusterTab('custom')}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${clusterTab === 'custom' ? 'border-[#2E75B6] text-[#1A3A5C]' : 'border-transparent text-[#64748B] hover:text-[#1A3A5C]'}`}
              data-testid="tab-custom">
              <PenLine className="w-3 h-3 inline mr-1" />Personalizzato
            </button>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {clusterTab === 'custom' ? (
              <div className="space-y-3" data-testid="custom-scenario-panel">
                <p className="text-xs text-[#64748B]">Descrivi liberamente lo scenario di test. Il tester AI eseguira la simulazione in base alla tua descrizione.</p>
                <textarea
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  placeholder="Es: Prova a creare un nuovo evento, aggiungere 2 speaker e verificare che il fill rate si aggiorni correttamente nella card..."
                  className="w-full h-32 text-sm border border-gray-200 rounded-lg p-2.5 resize-none focus:border-[#2E75B6] focus:ring-1 focus:ring-[#2E75B6] outline-none"
                  data-testid="custom-scenario-input"
                />
                <Button size="sm" className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90"
                  onClick={runCustomScenario} disabled={runningCustom || !customText.trim()}
                  data-testid="run-custom-btn">
                  {runningCustom ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  {runningCustom ? 'Esecuzione in corso...' : 'Esegui scenario personalizzato'}
                </Button>
              </div>
            ) : filteredScenarios.map(sc => {
              const persona = personas[sc.persona] || {};
              return (
                <div key={sc.id} onClick={() => { setSelectedScenario(sc.id); setSelectedSession(null); setSessionShownAt(null); }}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedScenario === sc.id ? 'border-[#2E75B6] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`scenario-${sc.id}`}>
                  <p className="text-sm font-medium text-[#1A3A5C]">{sc.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <User className="w-3 h-3 text-[#64748B]" />
                    <span className="text-xs text-[#64748B]">{persona.name?.split(' - ')[0] || sc.persona}</span>
                    <span className="text-xs text-[#64748B]">| {sc.steps_count} step</span>
                  </div>
                  <Button size="sm" className="mt-2 w-full" disabled={running === sc.id}
                    onClick={e => { e.stopPropagation(); runScenario(sc.id); }} data-testid={`run-${sc.id}`}>
                    {running === sc.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                    {running === sc.id ? 'In corso...' : 'Esegui'}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Center — Results */}
        <div className="col-span-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wide">Risultati</h3>
            {autoHideRemaining !== null && selectedSession && (
              <span className="text-[10px] text-[#94A3B8] flex items-center gap-1" data-testid="auto-hide-timer">
                <Clock className="w-3 h-3" />Nasconde in {autoHideRemaining} min
              </span>
            )}
          </div>
          {!currentSession ? (
            <Card><CardContent className="py-8 text-center text-sm text-[#64748B]">
              Seleziona uno scenario ed eseguilo per vedere i risultati
            </CardContent></Card>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="pt-3 pb-3 text-center">
                  <p className="text-2xl font-bold text-[#1A3A5C]">{result.overall_ux_score || '-'}</p>
                  <p className="text-xs text-[#64748B]">Punteggio UX</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3 text-center">
                  <p className="text-2xl font-bold text-[#1A3A5C]">{result.completion_rate ? `${Math.round(result.completion_rate * 100)}%` : '-'}</p>
                  <p className="text-xs text-[#64748B]">Completamento</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3 text-center">
                  <p className="text-2xl font-bold text-[#1A3A5C]">{result.steps?.length || '-'}</p>
                  <p className="text-xs text-[#64748B]">Step testati</p>
                </CardContent></Card>
              </div>

              {currentSession.id && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => downloadReport(currentSession.id, 'markdown')} data-testid="download-md" title="Scarica report">
                    <Download className="w-3 h-3 mr-1" />Report MD
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => downloadReport(currentSession.id, 'json')} data-testid="download-json" title="Scarica report">
                    <Download className="w-3 h-3 mr-1" />Report JSON
                  </Button>
                </div>
              )}

              {result.summary && (
                <Card><CardContent className="pt-4 pb-4"><p className="text-sm text-[#1A3A5C]">{result.summary}</p></CardContent></Card>
              )}

              {result.steps?.map(step => {
                const sev = getSeverityConfig(step.finding_severity);
                const SevIcon = sev.icon;
                return (
                  <div key={step.step_id} className="flex items-start gap-3 p-3 bg-white border rounded-lg" data-testid={`step-${step.step_id}`}>
                    <div className="mt-0.5"><SevIcon className={`w-4 h-4 ${step.finding_severity === 'ok' ? 'text-green-600' : step.finding_severity === 'blocking' ? 'text-red-600' : 'text-amber-600'}`} /></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${sev.color}`}>{getSeverityLabel(step.finding_severity)}</span>
                        <span className="text-xs text-[#64748B]">{step.clicks_estimated} click | ~{step.time_seconds_estimated}s</span>
                        <span className="text-xs text-[#64748B]">UX: {step.ux_score}/10</span>
                      </div>
                      {step.finding && <p className="text-sm text-[#1A3A5C] mt-1">{step.finding}</p>}
                      {step.improvement && <p className="text-xs text-[#2E75B6] mt-1">{step.improvement}</p>}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Sessions History */}
          {sessions.length > 0 && (
            <div className="space-y-2 mt-4">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wide">Storico sessioni</h3>
              {sessions.slice(0, 10).map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 bg-white border rounded text-xs"
                  data-testid={`session-${s.id}`}>
                  <div className="flex items-center gap-2 cursor-pointer flex-1"
                    onClick={() => { setSelectedSession(s); setSelectedScenario(s.scenario_id); setSessionShownAt(Date.now()); }}>
                    <span className="font-medium text-[#1A3A5C]">{s.scenario_name}</span>
                    <span className="text-[#64748B]">UX: {s.result?.overall_ux_score || '-'}</span>
                    <span className="text-[#94A3B8]">{s.run_at ? new Date(s.run_at).toLocaleDateString('it-CH') : ''}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5"
                    onClick={() => downloadReport(s.id, 'markdown')} title="Scarica">
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — Findings */}
        <div className="col-span-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wide">
              Problemi aperti ({findings.length})
            </h3>
            {findings.length > 0 && (
              <div className="relative" ref={exportRef}>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setExportMenuOpen(!exportMenuOpen)} data-testid="export-findings-btn">
                  <FileText className="w-3 h-3 mr-1" />Esporta <ChevronDown className="w-3 h-3 ml-0.5" />
                </Button>
                {exportMenuOpen && (
                  <div className="absolute right-0 top-8 z-20 bg-white border border-[#E2E8F0] rounded-lg shadow-lg py-1 min-w-[180px]" data-testid="export-menu">
                    <button onClick={() => exportFindings()} className="w-full text-left px-3 py-1.5 text-xs text-[#1A3A5C] hover:bg-[#F8FAFC]" data-testid="export-all">
                      Tutti i problemi
                    </button>
                    <button onClick={() => exportFindings('blocking')} className="w-full text-left px-3 py-1.5 text-xs text-red-700 hover:bg-red-50" data-testid="export-blocking">
                      Solo bloccanti ({severityCounts.blocking})
                    </button>
                    <button onClick={() => exportFindings('major')} className="w-full text-left px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-50" data-testid="export-major">
                      Solo rilevanti ({severityCounts.major})
                    </button>
                    <button onClick={() => exportFindings('minor')} className="w-full text-left px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-50" data-testid="export-minor">
                      Solo minori ({severityCounts.minor})
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Severity filter with counts */}
          <div className="flex gap-1 flex-wrap" data-testid="severity-filters">
            {Object.entries(SEVERITY_FILTER_MAP).map(([key, { label }]) => (
              <button key={key} onClick={() => setSeverityFilter(key)}
                className={`text-xs px-2 py-1 rounded transition-colors ${severityFilter === key ? 'bg-[#1A3A5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`filter-${key}`}>
                {label} ({severityCounts[key] || 0})
              </button>
            ))}
          </div>

          {filteredFindings.length === 0 ? (
            <Card><CardContent className="py-6 text-center text-sm text-[#64748B]">
              Nessun problema {severityFilter !== 'all' ? `di tipo "${SEVERITY_FILTER_MAP[severityFilter]?.label}"` : 'aperto'}
            </CardContent></Card>
          ) : filteredFindings.map(f => {
            const sev = getSeverityConfig(f.severity);
            const ctx = f.step_context || {};
            const isExpanded = expandedFinding === f.id;
            return (
              <div key={f.id} className="bg-white border rounded-lg overflow-hidden" data-testid={`finding-${f.id}`}>
                {/* Compact header — always visible */}
                <div className="flex items-start justify-between p-3 cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                  onClick={() => setExpandedFinding(isExpanded ? null : f.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${sev.color}`}>
                        {f.severity_label || getSeverityLabel(f.severity)}
                      </span>
                      <span className="text-[10px] text-[#94A3B8]">{f.category}</span>
                      {f.scenario_name && <span className="text-[10px] text-[#94A3B8]">| {f.scenario_name}</span>}
                    </div>
                    <p className="text-sm font-medium text-[#1A3A5C] mt-1">{f.title}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 items-center">
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                  </div>
                </div>

                {/* Expanded detail — full context */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-[#F1F5F9] space-y-2" data-testid={`finding-detail-${f.id}`}>
                    {/* Observation */}
                    {ctx.tester_finding && (
                      <div>
                        <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide mb-0.5">Osservazione tester</p>
                        <p className="text-xs text-[#334155] bg-[#F8FAFC] p-2 rounded leading-relaxed">{ctx.tester_finding}</p>
                      </div>
                    )}
                    {/* Improvement suggestion */}
                    {ctx.tester_improvement && (
                      <div>
                        <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide mb-0.5">Suggerimento miglioramento</p>
                        <p className="text-xs text-[#2E75B6] bg-blue-50/50 p-2 rounded leading-relaxed">{ctx.tester_improvement}</p>
                      </div>
                    )}
                    {/* Test context: goal, criteria, score */}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {ctx.goal && (
                        <div>
                          <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide">Obiettivo test</p>
                          <p className="text-xs text-[#475569]">{ctx.goal}</p>
                        </div>
                      )}
                      {ctx.success_criteria && (
                        <div>
                          <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wide">Criterio violato</p>
                          <p className="text-xs text-[#475569]">{ctx.success_criteria}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 pt-1 text-[10px] text-[#94A3B8]">
                      {ctx.ux_score !== undefined && <span>UX Score: <b className="text-[#1A3A5C]">{ctx.ux_score}/10</b></span>}
                      {ctx.clicks_estimated !== undefined && <span>Click stimati: <b className="text-[#1A3A5C]">{ctx.clicks_estimated}</b></span>}
                      {ctx.time_seconds_estimated !== undefined && <span>Tempo: <b className="text-[#1A3A5C]">~{ctx.time_seconds_estimated}s</b></span>}
                      {f.created_at && <span>{new Date(f.created_at).toLocaleDateString('it-CH')}</span>}
                    </div>
                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); moveToBacklog(f.id); }}
                        data-testid={`archive-${f.id}`}>
                        <Archive className="w-3 h-3 mr-1" />Archivia
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); deleteFinding(f.id); }}
                        data-testid={`delete-${f.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Elimina
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
